import { useState, useEffect, useCallback } from 'react';
import { adminOrderInvoicesApi, AdminInvoiceDocument } from '../../services/invoicesApi';
import { ApiError } from '../../services/api';
import InvoiceDocumentRow from './InvoiceDocumentRow';
import ResendInvoiceDialog from './ResendInvoiceDialog';
import GenerateInvoiceDialog from './GenerateInvoiceDialog';

interface InvoicePanelProps {
  orderId: string;
  orderPaid: boolean;
  token: string | null;
}

type Toast = { message: string; type: 'success' | 'error' };

/**
 * Spec 051 Flow C / AC-10, AC-11 — invoice oversight panel on the admin order
 * detail: document list with status + delivery chips, download via presigned
 * URL, resend with optional email override, manual generate for paid orders.
 */
export default function InvoicePanel({ orderId, orderPaid, token }: InvoicePanelProps) {
  const [documents, setDocuments] = useState<AdminInvoiceDocument[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [resendTarget, setResendTarget] = useState<AdminInvoiceDocument | null>(null);
  // #823: a permanent bounce or a provider suppression means the stored address will fail again,
  // so the dialog must insist on a different one rather than offering it as optional.
  const [resendRequiresOverride, setResendRequiresOverride] = useState(false);
  const [showGenerate, setShowGenerate] = useState(false);
  const [toast, setToast] = useState<Toast | null>(null);

  const showToast = (message: string, type: Toast['type']) => {
    setToast({ message, type });
    window.setTimeout(() => setToast(null), 4000);
  };

  const fetchInvoices = useCallback(() => {
    if (!token) return;
    setLoading(true);
    setError(null);
    adminOrderInvoicesApi.getInvoices(orderId, token)
      .then((res) => setDocuments(res.documents ?? []))
      .catch((err) => setError(err instanceof Error ? err.message : 'Failed to load invoices'))
      .finally(() => setLoading(false));
  }, [orderId, token]);

  useEffect(() => { fetchInvoices(); }, [fetchInvoices]);

  const handleDownload = async (doc: AdminInvoiceDocument) => {
    if (!token) return;
    try {
      const { downloadUrl } = await adminOrderInvoicesApi.getDownloadUrl(orderId, doc.id, token);
      window.open(downloadUrl, '_blank');
    } catch (err) {
      if (err instanceof ApiError && err.status === 409) {
        showToast('Invoice is being prepared', 'error');
      } else {
        showToast(err instanceof Error ? err.message : 'Download failed', 'error');
      }
    }
  };

  const handleResendConfirm = async (emailOverride: string | undefined) => {
    if (!token || !resendTarget) return;
    const result = await adminOrderInvoicesApi.resend(
      orderId, resendTarget.id, { emailOverride: emailOverride ?? null }, token,
    );
    setResendTarget(null);
    if (result.result === 'QUEUED') {
      // The send is asynchronous: the delivery log gains its row when notifications reports back.
      showToast('Resend queued — the delivery log updates within a minute', 'success');
      window.setTimeout(fetchInvoices, 5000);
    } else if (result.result === 'SENT') {
      showToast('Invoice re-sent successfully', 'success');
    } else {
      showToast(`Resend failed${result.error ? `: ${result.error}` : ''}`, 'error');
    }
    fetchInvoices();
  };

  const handleGenerateConfirm = async () => {
    if (!token) return;
    try {
      await adminOrderInvoicesApi.generate(orderId, token);
      setShowGenerate(false);
      showToast('Invoice generated', 'success');
      fetchInvoices();
    } catch (err) {
      setShowGenerate(false);
      if (err instanceof ApiError && err.status === 409) {
        showToast('Invoice already exists', 'error');
        fetchInvoices();
      } else {
        showToast(err instanceof Error ? err.message : 'Generation failed', 'error');
      }
    }
  };

  // Manual generation (AC-11) is about the GARAGE's invoice; Garagat's own PLT is issued by the
  // payment hook and has no manual path, so a PLT present alone must not hide the button.
  const hasInvoice = documents.some((d) => d.docType === 'INVOICE');
  const canGenerate = !loading && !error && !hasInvoice && orderPaid;

  return (
    <div className="admin-card" style={{ marginBottom: '1rem' }}>
      <div className="invoice-panel-header">
        <h3 className="admin-card-title" style={{ margin: 0 }}>Invoices</h3>
        {canGenerate && (
          <button className="admin-btn admin-btn-primary admin-btn-sm" onClick={() => setShowGenerate(true)}>
            Generate invoice
          </button>
        )}
      </div>

      {loading ? (
        <p style={{ fontSize: '0.875rem', color: 'var(--admin-text-muted)', margin: 0 }}>
          Loading invoices...
        </p>
      ) : error ? (
        <div className="admin-info-banner danger">
          {error}{' '}
          <button type="button" className="invoice-link-btn" onClick={fetchInvoices}>Retry</button>
        </div>
      ) : documents.length === 0 ? (
        <div className="admin-empty-state">
          <p>{orderPaid ? 'No invoice has been issued for this order yet' : 'No invoice'}</p>
        </div>
      ) : (
        <div className="invoice-doc-list">
          {documents.map((doc) => (
            <InvoiceDocumentRow
              key={doc.id}
              doc={doc}
              onDownload={handleDownload}
              onResend={(doc, forceOverride) => {
                setResendRequiresOverride(forceOverride);
                setResendTarget(doc);
              }}
            />
          ))}
        </div>
      )}

      {resendTarget && (
        <ResendInvoiceDialog
          doc={resendTarget}
          requireOverride={resendRequiresOverride}
          onConfirm={handleResendConfirm}
          onClose={() => setResendTarget(null)}
        />
      )}
      {showGenerate && (
        <GenerateInvoiceDialog onConfirm={handleGenerateConfirm} onClose={() => setShowGenerate(false)} />
      )}

      {toast && <div className={`admin-toast ${toast.type}`}>{toast.message}</div>}
    </div>
  );
}
